import React, { useState, useEffect } from "react";
import {
  Button,
  Grid,
  Typography,
  Paper,
  Chip,
  Box,
  Card,
  CardContent,
  Divider,
  Stack,
  CircularProgress,
  alpha,
  Switch,
  FormControlLabel,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useParams, useHistory } from "react-router-dom";
import AutoCompleteFeild from "../../../FormComponents/AutoCompleteFeild";
import InputField from "../../../FormComponents/InputField";
import {
  getAllImportData,
  getCategoryById,
  getImportItemById,
  updateIm_Item,
} from "../../../services/Import";
import Validation from "../../../Validation/Validation";
import FormAlert from "../../../components/FormAlert";
import Im_CategorySelectingPopup from "../../../components/Im_CategorySelectingPopup";

// Icons
import InventoryIcon from "@mui/icons-material/Inventory";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SaveIcon from "@mui/icons-material/Save";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import CategoryIcon from "@mui/icons-material/Category";
import AddIcon from "@mui/icons-material/Add";

// Custom styled components
const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  margin: theme.spacing(3),
  borderRadius: theme.shape.borderRadius * 2,
  boxShadow: "0 4px 20px 0 rgba(0,0,0,0.05)",
  backgroundColor: theme.palette.background.paper,
}));

const HeaderCard = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: theme.spacing(3),
  padding: theme.spacing(2),
  borderRadius: theme.shape.borderRadius * 1.5,
  backgroundColor: alpha(theme.palette.primary.main, 0.05),
}));

const ActionButton = styled(Button)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius * 1.5,
  textTransform: "none",
  fontWeight: 600,
  boxShadow: "0 4px 12px 0 rgba(0,0,0,0.1)",
}));

const FormCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  borderRadius: theme.shape.borderRadius * 2,
  boxShadow: "0 2px 8px 0 rgba(0,0,0,0.05)",
}));

const CategoryCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: theme.shape.borderRadius * 1.5,
  border: `2px dashed ${alpha(theme.palette.primary.main, 0.3)}`,
  backgroundColor: alpha(theme.palette.primary.main, 0.02),
  cursor: "pointer",
  transition: "all 0.2s ease-in-out",
  "&:hover": {
    borderColor: theme.palette.primary.main,
    backgroundColor: alpha(theme.palette.primary.main, 0.05),
    transform: "translateY(-2px)",
    boxShadow: "0 4px 12px 0 rgba(0,0,0,0.08)",
  },
}));

const StyledChip = styled(Chip)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius,
  fontWeight: 500,
  margin: theme.spacing(0.5),
}));

function Im_ItemEdit() {
  const history = useHistory();
  const { importId } = useParams();

  // State for loading
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // For input field values
  const [value, setValue] = useState({
    _importName: "",
    _importBrand: "",
    _productCode: "",
    _categories: [],
    _importUnitType: "",
    _importNote: "",
    _refillRate: 0,
    _qty: 0,
    _status: "ACTIVE",
  });

  // Category management
  const [openCatgoryTable, setOpenCatgoryTable] = useState(false);
  const [selectedCatgory, setSelectedCatgory] = useState([]);
  const [importCategories, setImportCategories] = useState([]);
  const [catgoryRows, setCatgoryRows] = useState([]);

  // Import items dataset
  const [itemSet, setItem] = useState([]);

  // Errors for input fields
  const [error, setError] = useState({
    _importName: "",
    _importBrand: "",
    _importMCategory: "",
    _importQty: "",
    _importUnitType: "",
    _refillRate: 0,
  });

  // Display alert
  const [displayAlert, setAlert] = useState(false);
  const [alertData, setAlertData] = useState({
    type: "",
    message: "",
  });

  // Initial data fetch
  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      let importItemData = await getImportItemById(importId);
      let categoryIdList = [];
      let categoryList = importItemData.im_categories;

      for (let i = 0; i < categoryList.length; i++) {
        categoryIdList.push(categoryList[i].cat_id);
      }

      setSelectedCatgory(categoryIdList);
      setValue({
        _importName: importItemData.itemName,
        _importBrand: importItemData.brand,
        _productCode: importItemData.product_code,
        _categories: categoryIdList,
        _importUnitType: importItemData.unitType,
        _importNote: importItemData.note,
        _refillRate: importItemData.refillRate,
        _qty: importItemData.qty,
        _status: importItemData.status || "ACTIVE",
      });
      setImportCategories(importItemData.im_categories);
    } catch (error) {
      console.error("Error fetching import item data:", error);
      setAlertData({
        type: "error",
        message: "Failed to load import item data",
      });
      setAlert(true);
    } finally {
      setIsLoading(false);
    }
  };

  // Reset values
  const resetValues = () => {
    fetchData(); // Reload original data
  };

  // Category table handlers
  const handleOpenCategoryTable = async () => {
    setOpenCatgoryTable(true);
  };

  const handleCloseCatgoryTable = async () => {
    const categoryList = [];
    for (let x = 0; x < selectedCatgory.length; x++) {
      let categoryData = await getCategoryById(selectedCatgory[x]);
      categoryList.push(categoryData);
    }
    setImportCategories(categoryList);
    setValue({
      ...value,
      _categories: selectedCatgory,
    });
    setOpenCatgoryTable(false);
  };

  // Alert management
  const closeAlert = () => {
    setAlert(false);
    setAlertData({
      type: "",
      message: "",
    });
  };

  // Reset errors
  const resetErrors = () => {
    setError({});
  };

  // Submit value
  const submitValue = async () => {
    setError(Validation(value));

    if (
      !error._importName &&
      !error._importQty &&
      !error._importUnitType &&
      !error._importMCategory &&
      !error._importBrand
    ) {
      setIsSubmitting(true);
      try {
        let data = { ...value };
        let submit_import_data = await updateIm_Item(data, importId);

        if (submit_import_data) {
          setAlertData({
            type: "success",
            message: "Item updated successfully!",
          });
          setAlert(true);
          setTimeout(() => {
            history.goBack();
          }, 1500);
        } else {
          setAlertData({
            type: "error",
            message: "Something went wrong...",
          });
          setAlert(true);
        }
      } catch (error) {
        console.error("Error updating item:", error);
        setAlertData({
          type: "error",
          message: "Failed to update item",
        });
        setAlert(true);
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  // Handle change
  const handleChange = (name, val) => {
    setValue({
      ...value,
      [name]: val,
    });
  };

  // Load import data
  const loadData = async () => {
    resetErrors();
    closeAlert();
    let importSet = await getAllImportData();
    setItem(importSet);
  };

  // Handle navigate back
  const handleGoBack = () => {
    history.goBack();
  };

  if (isLoading) {
    return (
      <StyledPaper elevation={3}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "400px",
            flexDirection: "column",
            gap: 2,
          }}
        >
          <CircularProgress />
          <Typography color="text.secondary">
            Loading import item data...
          </Typography>
        </Box>
      </StyledPaper>
    );
  }

  return (
    <StyledPaper elevation={3}>
      {/* Header */}
      <HeaderCard>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <InventoryIcon fontSize="large" color="primary" />
          <Box>
            <Typography variant="h5" fontWeight="600" color="primary.main">
              Update Import Item
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Modify import item information and categories
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <FormControlLabel
            control={
              <Switch
                checked={value._status === "ACTIVE"}
                onChange={(e) =>
                  handleChange(
                    "_status",
                    e.target.checked ? "ACTIVE" : "DEACTIVE"
                  )
                }
                color="success"
              />
            }
            label={
              <Typography
                variant="body2"
                fontWeight="600"
                color={
                  value._status === "ACTIVE" ? "success.main" : "error.main"
                }
              >
                {value._status === "ACTIVE" ? "Active" : "Inactive"}
              </Typography>
            }
          />
          <ActionButton
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={handleGoBack}
          >
            Back
          </ActionButton>
        </Box>
      </HeaderCard>

      <Divider sx={{ mb: 3 }} />

      {/* Alert */}
      {displayAlert && (
        <Box mb={3}>
          <FormAlert type={alertData.type} message={alertData.message} />
        </Box>
      )}

      {/* Form Content */}
      <FormCard>
        <CardContent>
          <Typography variant="h6" fontWeight="600" mb={3}>
            Basic Information
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <AutoCompleteFeild
                name="_importName"
                _key="itemName"
                value={value._importName}
                dataSet={itemSet}
                label="Import Name"
                onClick={loadData}
                errorMsg={error._importName}
                onchange={(event, newInputValue) =>
                  handleChange(event, newInputValue)
                }
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <AutoCompleteFeild
                name="_importBrand"
                _key="brand"
                value={value._importBrand}
                dataSet={itemSet}
                label="Brand"
                errorMsg={error._importBrand}
                onClick={loadData}
                onchange={(event, newInputValue) =>
                  handleChange(event, newInputValue)
                }
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <InputField
                name="_productCode"
                value={value._productCode}
                onChange={(event, newInputValue) =>
                  handleChange(event, newInputValue)
                }
                type="text"
                label="Product Code"
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <AutoCompleteFeild
                name="_importUnitType"
                _key="unitType"
                value={value._importUnitType}
                dataSet={itemSet}
                label="Unit Type"
                onClick={loadData}
                errorMsg={error._importUnitType}
                onchange={(event, newInputValue) =>
                  handleChange(event, newInputValue)
                }
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <InputField
                name="_refillRate"
                value={value._refillRate}
                onChange={(event, newInputValue) =>
                  handleChange(event, newInputValue)
                }
                type="number"
                label="Refill Rate"
              />
            </Grid>

            <Grid item xs={12}>
              <InputField
                name="_importNote"
                value={value._importNote}
                onChange={(event, newInputValue) =>
                  handleChange(event, newInputValue)
                }
                type="text"
                label="Note"
                multiline={true}
                rows={3}
              />
            </Grid>
          </Grid>
        </CardContent>
      </FormCard>

      {/* Category Section */}
      <FormCard>
        <CardContent>
          <Typography variant="h6" fontWeight="600" mb={2}>
            Categories
          </Typography>

          {importCategories.length !== 0 ? (
            <CategoryCard onClick={handleOpenCategoryTable} elevation={0}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                mb={1}
              >
                <Stack direction="row" alignItems="center" spacing={1}>
                  <CategoryIcon color="primary" />
                  <Typography variant="subtitle1" fontWeight="600">
                    Selected Categories
                  </Typography>
                </Stack>
                <Typography variant="caption" color="text.secondary">
                  Click to modify
                </Typography>
              </Stack>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 2 }}>
                {importCategories.map((category, index) => (
                  <StyledChip
                    key={index}
                    label={category.category}
                    color="primary"
                    variant="outlined"
                  />
                ))}
              </Box>
            </CategoryCard>
          ) : (
            <CategoryCard onClick={handleOpenCategoryTable} elevation={0}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  py: 3,
                }}
              >
                <CategoryIcon
                  sx={{
                    fontSize: 48,
                    color: "primary.main",
                    opacity: 0.5,
                    mb: 1,
                  }}
                />
                <Typography variant="body1" color="text.secondary" mb={1}>
                  No categories selected
                </Typography>
                <ActionButton
                  variant="outlined"
                  size="small"
                  startIcon={<AddIcon />}
                >
                  Select Categories
                </ActionButton>
              </Box>
            </CategoryCard>
          )}
        </CardContent>
      </FormCard>

      {/* Action Buttons */}
      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 3 }}>
        <ActionButton
          variant="outlined"
          startIcon={<RestartAltIcon />}
          onClick={resetValues}
          disabled={isSubmitting}
        >
          Reset
        </ActionButton>
        <ActionButton
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={submitValue}
          disabled={isSubmitting}
        >
          {isSubmitting ? "Updating..." : "Update Item"}
        </ActionButton>
      </Box>

      {/* Category Selection Popup */}
      {openCatgoryTable && (
        <Im_CategorySelectingPopup
          setOpenCatgoryTable={setOpenCatgoryTable}
          openCatgoryTable={openCatgoryTable}
          handleCloseCatgoryTable={handleCloseCatgoryTable}
          setSelectedCatgory={setSelectedCatgory}
          selectedCatgory={selectedCatgory}
          setCatgoryRows={setCatgoryRows}
          catgoryRows={catgoryRows}
        />
      )}
    </StyledPaper>
  );
}

export default Im_ItemEdit;
